import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, get } from 'react-hook-form';
import {
  Divider,
  Stack,
  DialogContent,
  DialogActions,
  Dialog,
  Button,
  MenuItem,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { yupResolver } from '@hookform/resolvers/yup';

import { Strings } from '@app/constants';
import { Header } from '@app/sections/header';
import { FormProvider, RHFSelect, RHFTextField, Scrollbar } from '@app/components';
import { IAddTestimonialSchema, ITestimonial, IClient } from '../types';
import { useRequestState } from '@app/hooks';
import { selectClientState, getClients } from '@app/modules/clients';
import { addTestimonial, updateTestimonial } from '../slice';
import { selectAddTestimonialState, selectUpdateTestimonialState } from '../selector';
import { addTestimonialSchema } from '../validators';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  row?: ITestimonial;
}

const AddTestimonial = ({ open, onClose, row }: Props) => {
  const dispatch = useDispatch();

  const methods = useForm<IAddTestimonialSchema>({
    resolver: yupResolver(addTestimonialSchema) as any,
    defaultValues: {
      clientId: get(row, 'clientId', ''),
      projectName: get(row, 'projectName', ''),
      projectLink: get(row, 'projectLink', ''),
      feedback: get(row, 'feedback', ''),
    },
  });

  const onSubmit = (form: IAddTestimonialSchema) => {
    row
      ? dispatch(updateTestimonial({ ...form, id: row?.id }))
      : dispatch(addTestimonial({ ...form }));
  };

  useEffect(() => {
    dispatch(getClients());
  }, [dispatch]);

  const { loading: saveTestimonial } = useRequestState({
    stateSelector: row ? selectUpdateTestimonialState : selectAddTestimonialState,
    onSuccess: onClose,
    successMessageShown: true,
    errorShown: true,
  });

  const { data: clients } = useRequestState({
    stateSelector: selectClientState,
    errorShown: true,
  });

  return (
    <>
      <FormProvider methods={methods}>
        <Dialog open={open}>
          <Header
            variant="dialog"
            title={row ? Strings.clients.updateClientTestimonial : Strings.clients.addTestimonial}
            onClose={onClose}
          />
          <Divider />
          <Scrollbar>
            <DialogContent>
              <Stack my={3} justifyContent={'space-between'} direction={'row'} spacing={2}>
                <RHFSelect required name="clientId" label={Strings.clients.clients} fullWidth>
                  {clients?.map((client: IClient) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.firstName} {client.lastName}
                    </MenuItem>
                  ))}
                </RHFSelect>
              </Stack>
              <Stack my={2} justifyContent={'space-between'} direction={'row'} spacing={2}>
                <RHFTextField
                  required
                  label={Strings.field.projectName}
                  name="projectName"
                  fullWidth
                />
                <RHFTextField
                  required
                  label={Strings.field.projectLink}
                  name="projectLink"
                  fullWidth
                />
              </Stack>
              <Stack my={2} justifyContent={'space-between'} direction={'row'} spacing={2}>
                <RHFTextField
                  required
                  multiline
                  name="feedback"
                  label={Strings.field.feedback}
                  variant="outlined"
                  minRows={3}
                  inputProps={{ maxLength: 310 }}
                />
              </Stack>
            </DialogContent>
          </Scrollbar>
          <Divider sx={{ pt: 2 }} />
          <DialogActions>
            <Button variant="outlined" color="inherit" onClick={onClose}>
              {Strings.button.cancel}
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              type="submit"
              loading={saveTestimonial}
              onClick={methods.handleSubmit(onSubmit)}
            >
              {Strings.button.save}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </FormProvider>
    </>
  );
};

export { AddTestimonial };
